import React, { useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import {
  Code2,
  Palette,
  TrendingUp,
  Globe,
  Server,
  Shield,
  Zap,
  Cpu,
  Database,
  Smartphone,
  Search,
  Users,
} from "lucide-react";

const skillCategories = [
  {
    title: "Frontend Development",
    icon: <Code2 />,
    color: "from-blue-500 to-cyan-500",
    skills: [
      { name: "React.js", level: 90 },
      { name: "JavaScript", level: 85 },
      { name: "HTML/CSS", level: 95 },
      { name: "Tailwind CSS", level: 92 },
      { name: "Bootstrap", level: 88 },
    ],
  },
  {
    title: "Digital Marketing",
    icon: <TrendingUp />,
    color: "from-purple-500 to-pink-500",
    skills: [
      { name: "SEO Strategy", level: 88 },
      { name: "Google Analytics", level: 85 },
      { name: "Social Media", level: 82 },
      { name: "Content Marketing", level: 80 },
      { name: "Email Marketing", level: 78 },
    ],
  },
  {
    title: "Networking",
    icon: <Server />,
    color: "from-green-500 to-emerald-500",
    skills: [
      { name: "CCNP", level: 90 },
      { name: "Network Security", level: 85 },
      { name: "LAN/WAN", level: 88 },
      { name: "IP Addressing", level: 92 },
      { name: "Network Troubleshooting", level: 87 },
    ],
  },
  {
    title: "UI/UX Design",
    icon: <Palette />,
    color: "from-orange-500 to-red-500",
    skills: [
      { name: "Canva", level: 75 },
      { name: "Responsive Design", level: 90 },
      { name: "User Research", level: 70 },
      { name: "Qulity Code", level: 99 },
      { name: "Content Createt", level: 90 },
    ],
  },
  {
    title: "Tools & Platforms",
    icon: <Cpu />,
    color: "from-yellow-500 to-amber-500",
    skills: [
      { name: "WordPress", level: 85 },
      { name: "Git", level: 80 },
      { name: "Canva", level: 75 },
    ],
  },
  {
    title: "Soft Skills",
    icon: <Users />,
    color: "from-indigo-500 to-violet-500",
    skills: [
      { name: "Project Management", level: 85 },
      { name: "Client Communication", level: 90 },
      { name: "Problem Solving", level: 88 },
      { name: "Team Collaboration", level: 87 },
      { name: "Time Management", level: 90 },
    ],
  },
];

const SkillBar = ({ skill, isHovered, index }) => {
  const controls = useAnimation();

  useEffect(() => {
    if (isHovered) {
      controls.start({
        width: `${skill.level}%`,
        transition: { duration: 1, ease: "easeOut" },
      });
    }
  }, [isHovered, skill.level, controls]);

  return (
    <div className="mb-4">
      <div className="flex justify-between mb-2">
        <span className="text-gray-300">{skill.name}</span>
        <span className="text-primary-400 font-semibold">{skill.level}%</span>
      </div>
      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={controls}
          className={`h-full rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 relative`}
        >
          {/* Shimmer Effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
        </motion.div>
      </div>
    </div>
  );
};

const SkillCategory = ({ category, isHovered, onHover }) => {
  const [flipped, setFlipped] = useState(false);

  return (
    <motion.div
      whileHover={{ y: -10, scale: 1.02 }}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      onClick={() => setFlipped(!flipped)}
      className="relative h-64 cursor-pointer"
    >
      {/* 3D Flip Container */}
      <div
        className={`relative w-full h-full transition-transform duration-700 ${
          flipped ? "rotate-y-180" : ""
        }`}
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* Front Side */}
        <div className="absolute inset-0 backface-hidden">
          <div
            className={`p-6 rounded-2xl glass-effect border border-white/10 h-full`}
          >
            {/* Icon */}
            <div
              className={`p-3 rounded-xl bg-gradient-to-br ${category.color} w-14 h-14 flex items-center justify-center mb-4`}
            >
              {category.icon}
            </div>

            {/* Title */}
            <h3 className="text-xl font-bold mb-4">{category.title}</h3>

            {/* Skills Preview */}
            <div className="space-y-2">
              {category.skills.slice(0, 3).map((skill, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">{skill.name}</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-white/10">
                    {skill.level}%
                  </span>
                </div>
              ))}
            </div>

            {/* Flip Hint */}
            <div className="absolute bottom-4 right-4 text-xs text-gray-500">
              Click to flip
            </div>
          </div>
        </div>

        {/* Back Side */}
        <div
          className="absolute inset-0 backface-hidden rotate-y-180"
          style={{ backfaceVisibility: "hidden" }}
        >
          <div
            className={`p-6 rounded-2xl bg-gradient-to-br ${category.color} h-full`}
          >
            <h3 className="text-xl font-bold mb-4 text-white">Skill Details</h3>
            <div className="space-y-3">
              {category.skills.map((skill, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-white/90">{skill.name}</span>
                  <div className="w-24 h-2 rounded-full bg-white/30 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-white"
                      style={{ width: `${skill.level}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="absolute bottom-4 right-4 text-xs text-white/70">
              Click to return
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Skills = () => {
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(0);

  return (
    <section id="skills" className="py-20 relative">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-900/10 via-transparent to-secondary-900/10"></div>
      <div className="absolute top-20 left-1/4 w-64 h-64 bg-primary-500/5 rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-20 right-1/4 w-64 h-64 bg-secondary-500/5 rounded-full blur-3xl animate-float delay-1000"></div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-effect border border-white/10 mb-4">
            <Zap className="w-4 h-4 text-primary-400" />
            <span className="text-sm">Technical Expertise</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Professional
            <span className="block gradient-text">Skills</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            A versatile skill set spanning development, marketing, and
            networking
          </p>
        </motion.div>

        {/* Skills Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {skillCategories.map((category, index) => (
            <SkillCategory
              key={index}
              category={category}
              isHovered={hoveredCategory === index}
              onHover={(isHovering) =>
                setHoveredCategory(isHovering ? index : null)
              }
            />
          ))}
        </div>

        {/* Detailed Skill View */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="rounded-2xl glass-effect border border-white/10 p-8"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Skill Bars */}
            <div>
              <h3 className="text-2xl font-bold mb-6">
                {skillCategories[selectedCategory].title} Skills
              </h3>
              <div className="space-y-6">
                {skillCategories[selectedCategory].skills.map(
                  (skill, index) => (
                    <SkillBar
                      key={index}
                      skill={skill}
                      isHovered={hoveredCategory === selectedCategory}
                      index={index}
                    />
                  )
                )}
              </div>
            </div>

            {/* Right Column - Skill Description */}
            <div>
              <h3 className="text-2xl font-bold mb-6">Proficiency Areas</h3>
              <div className="space-y-4">
                {[
                  "Advanced React.js development with modern hooks",
                  "SEO optimization and digital strategy",
                  "Network infrastructure and security",
                  "Responsive web design principles",
                  "Client communication and project management",
                ].map((area, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start space-x-3 p-3 rounded-lg bg-white/5"
                  >
                    <div className="w-2 h-2 rounded-full bg-primary-500 mt-2"></div>
                    <span className="text-gray-300">{area}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* CSS for 3D Flip */}
      <style jsx>{`
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
        .backface-hidden {
          backface-visibility: hidden;
        }
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </section>
  );
};

export default Skills;
